import { CanceledError } from 'axios';

import QueryParams from '@/common/models/query-params/QueryParams';
import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { isObjectEmpty } from '@/globals';
import API from '@/org-app/api';
import { ApiClass, Delete, EntityWithID, GetAll, GetOne, IPaginationResult, Post, Put } from '@/utils/ApiUtils';
import i18n from '@/vueI18n';

export function handleError(
  errorMessageKey,
  originRoute,
  showLoader,
  shouldWarnUserOnError,
  err,
  forceWarnUserOnError?,
) {
  const msg = errorMessageKey ? i18n.global.t(errorMessageKey) : i18n.global.t('messages.somethingWentWrong');
  const isCurrentRouteSameAsOrigin = originRoute === window.location.pathname;

  if (showLoader) {
    GlobalEventBus.$emit(GlobalEvents.loaded);
  }

  if (!(err instanceof CanceledError) && shouldWarnUserOnError && isCurrentRouteSameAsOrigin) {
    ErrorHandler.showError(msg, err, forceWarnUserOnError);
  } else if (isCurrentRouteSameAsOrigin) {
    throw err;
  }
}

type GetAllActionWrapper<T> = (
  { commit },
  config?: {
    queryParams?: QueryParams;
    cancelTokenSource?;
    showLoader?: boolean;
    shouldWarnUserOnError?: boolean;
    forceWarnUserOnError?: boolean;
  },
) => Promise<IPaginationResult<T>>;

export interface IGetAllActionParams {
  queryParams?: QueryParams;
  cancelTokenSource?;
  showLoader?: boolean;
  shouldWarnUserOnError?: boolean;
  forceWarnUserOnError?: boolean;
}

export function createGetAllAction<T>(
  apiMethod: GetAll<T>,
  mutationName,
  errorMessageKey,
  showLoaderDefault = true,
  postMutationFunction = null,
  defaultQueryParams = null,
  isResultWrappedInItemsCollection = true,
): GetAllActionWrapper<T> {
  return async (
    { commit },
    {
      queryParams = defaultQueryParams ?? QueryParams.all(),
      cancelTokenSource = null,
      showLoader = showLoaderDefault,
      shouldWarnUserOnError = true,
      forceWarnUserOnError = false,
    } = {} as IGetAllActionParams,
  ) => {
    const originRoute = window.location.pathname;

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const instances = await apiMethod
      .bind(API.instance)({ queryParams, cancelTokenSource })
      .catch((err) => {
        handleError(errorMessageKey, originRoute, showLoader, shouldWarnUserOnError, err, forceWarnUserOnError);
      });

    if (instances) {
      if (mutationName) {
        commit(mutationName, isResultWrappedInItemsCollection ? instances.items : instances);
      }
      if (postMutationFunction) {
        postMutationFunction(commit, instances);
      }

      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      return instances;
    }
    return null;
  };
}

type GetOneActionWrapper<T> = (
  { commit },
  config: {
    id: number;
    queryParams?: QueryParams;
    cancelTokenSource?;
    showLoader?: boolean;
    shouldWarnUserOnError?: boolean;
    forceWarnUserOnError?: boolean;
  },
) => Promise<T>;

export function createGetOneAction<T>(
  apiMethod: GetOne<T>,
  mutationName,
  errorMessageKey,
  showLoaderDefault = true,
  postMutationFunction = null,
): GetOneActionWrapper<T> {
  return async (
    { commit },
    {
      id,
      queryParams = QueryParams.all(),
      cancelTokenSource = null,
      showLoader = showLoaderDefault,
      shouldWarnUserOnError = true,
      forceWarnUserOnError = false,
    },
  ) => {
    const originRoute = window.location.pathname;

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }

    const instance = await apiMethod
      .bind(API.instance)({
        id,
        queryParams,
        cancelTokenSource,
      })
      .catch((err) => {
        handleError(errorMessageKey, originRoute, showLoader, shouldWarnUserOnError, err, forceWarnUserOnError);
      });

    if (instance) {
      if (mutationName) {
        commit(mutationName, instance);
      }

      if (postMutationFunction) {
        postMutationFunction(commit, instance);
      }

      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      return instance;
    }
    return null;
  };
}

export type PostActionWrapper<T> = <Instance extends T | T[]>(
  { commit },
  config: {
    instances: Instance;
    queryParams?: QueryParams;
    showLoader?: boolean;
    shouldWarnUserOnError?: boolean;
    forceWarnUserOnError?: boolean;
  },
) => Promise<Instance extends T[] ? T[] : T>;

export type MappedPostAction<T> = <Instance extends T | T[]>(config: {
  instances: Instance;
  queryParams?: QueryParams;
  showLoader?: boolean;
  shouldWarnUserOnError?: boolean;
  forceWarnUserOnError?: boolean;
}) => Promise<Instance extends T[] ? T[] : T>;

export function createPostAction<T extends ApiClass>(
  apiMethod: Post<T>,
  mutationName,
  errorMessageKey,
  showLoaderDefault = true,
  postMutationFunction = null,
): PostActionWrapper<T> {
  // Instances can either be one instance or an array of instances. It will return either an array or a single instance depending on the input.
  // The mutation should also be able to accept either one or multiple instances.
  return async (
    { commit },
    {
      instances,
      queryParams = QueryParams.all(),
      showLoader = showLoaderDefault,
      shouldWarnUserOnError = true,
      forceWarnUserOnError = false,
    },
  ) => {
    const originRoute = window.location.pathname;
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    try {
      const saved = await apiMethod.bind(API.instance)({ instances, queryParams });

      if (mutationName) {
        commit(mutationName, saved);
      }

      if (postMutationFunction) {
        postMutationFunction(commit, saved);
      }

      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      return saved;
    } catch (err) {
      handleError(errorMessageKey, originRoute, showLoader, shouldWarnUserOnError, err, forceWarnUserOnError);
      return null;
    }
  };
}

type PutActionWrapper<T> = (
  { commit },
  config: {
    instance: T;
    queryParams?: QueryParams;
    showLoader?: boolean;
    shouldWarnUserOnError?: boolean;
    forceWarnUserOnError?: boolean;
  },
) => Promise<T>;

export function createPutAction<T extends ApiClass>(
  apiMethod: Put<T>,
  mutationName,
  errorMessageKey,
  showLoaderDefault = true,
  postMutationFunction = null,
): PutActionWrapper<T> {
  return async (
    { commit },
    {
      instance,
      queryParams = QueryParams.all(),
      showLoader = showLoaderDefault,
      shouldWarnUserOnError = true,
      forceWarnUserOnError = false,
    },
  ) => {
    const originRoute = window.location.pathname;
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    try {
      const saved = await apiMethod.bind(API.instance)({ instance, queryParams });

      if (mutationName) {
        commit(mutationName, saved);
      }

      if (postMutationFunction) {
        postMutationFunction(commit, saved);
      }

      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      if (instance?.isInlineEdited && !saved) {
        return null;
      }

      return saved;
    } catch (err) {
      if (instance?.isInlineEdited) {
        GlobalEventBus.$emit(GlobalEvents.inlineEditError);
      }
      handleError(errorMessageKey, originRoute, showLoader, shouldWarnUserOnError, err, forceWarnUserOnError);
      return null;
    }
  };
}

type DeleteActionWrapper = (
  { commit },
  config: {
    instance: EntityWithID;
    queryParams?: QueryParams;
    showLoader?: boolean;
    shouldWarnUserOnError?: boolean;
    forceWarnUserOnError?: boolean;
  },
) => Promise<void>;

export function createDeleteAction(
  apiMethod: Delete,
  mutationName,
  errorMessageKey,
  showLoaderDefault = true,
  postMutationFunction = null,
): DeleteActionWrapper {
  return async (
    { commit },
    { instance, showLoader = showLoaderDefault, shouldWarnUserOnError = true, forceWarnUserOnError = false },
  ) => {
    const originRoute = window.location.pathname;

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }

    await apiMethod
      .bind(API.instance)({ instance })
      .catch((err) => {
        handleError(errorMessageKey, originRoute, showLoader, shouldWarnUserOnError, err, forceWarnUserOnError);
      });

    if (mutationName) {
      commit(mutationName, instance);
    }

    if (postMutationFunction) {
      postMutationFunction(commit, instance);
    }

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return null;
  };
}

export function createAuditAction(apiMethod, errorMessageKey, showLoaderDefault = true) {
  return async (_, { id, showLoader = showLoaderDefault, shouldWarnUserOnError = true }) => {
    const originRoute = window.location.pathname;

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }

    const trail = await apiMethod
      .bind(API.instance)({ id })
      .catch((err) => {
        handleError(errorMessageKey, originRoute, showLoader, shouldWarnUserOnError, err);
      });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return trail;
  };
}

export function createApiAction(
  apiMethod,
  mutationName,
  errorMessageKey,
  showLoaderDefault = true,
  dataTransform = null,
  isRootMutation = false,
  mutationValueTransform = null, // (params, response) => {}
) {
  return async (
    { commit },
    {
      params = {},
      data = {},
      cancelTokenSource = null,
      showLoader = showLoaderDefault,
      urlSuffix = null,
      shouldWarnUserOnError = true,
      forceWarnUserOnError = false,
    } = {},
  ) => {
    const originRoute = window.location.pathname;

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }

    const response = await apiMethod
      .bind(API.instance)({
        params,
        data,
        cancelTokenSource,
        urlSuffix,
        shouldWarnUserOnError,
      })
      .catch((err) => {
        handleError(errorMessageKey, originRoute, showLoader, shouldWarnUserOnError, err, forceWarnUserOnError);
      });
    if (response != null) {
      if (mutationName) {
        let value = response;
        if (mutationValueTransform) {
          value = mutationValueTransform(params, response);
        }
        commit(mutationName, value, { root: isRootMutation });
      }

      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      return dataTransform != null ? dataTransform(response) : response;
    }
    return null;
  };
}

export function createPatchAction(
  apiMethod,
  errorMessages = null, // { overall, adding, updating, deleting }
  showLoaderDefault = true,
  dataTransform = null,
) {
  return async (
    { commit },
    {
      params = {},
      toAdd = [],
      toUpdate = {},
      toDelete = [],
      cancelTokenSource = null,
      showLoader = showLoaderDefault,
      urlSuffix = null,
      shouldWarnUserOnError = true,
    } = {},
  ) => {
    const originRoute = window.location.pathname;

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const data = {
      to_add: toAdd,
      to_update: toUpdate,
      to_delete: toDelete,
    };
    try {
      const response = await apiMethod.bind(API.instance)({
        params,
        data,
        cancelTokenSource,
        urlSuffix,
        shouldWarnUserOnError,
      });
      if (response) {
        return dataTransform != null ? dataTransform(response) : response;
      }
    } catch (err) {
      let msg = null;
      const isCurrentRouteSameAsOrigin = originRoute === window.location.pathname;
      if (errorMessages) {
        const isTryingToAdd = !isObjectEmpty(toAdd);
        const isTryingToUpdate = !isObjectEmpty(toUpdate);
        const isTryingToDelete = toDelete.length > 0;
        if (errorMessages.adding && isTryingToAdd && !isTryingToUpdate && !isTryingToDelete) {
          msg = i18n.global.t(errorMessages.adding);
        } else if (errorMessages.updating && !isTryingToAdd && isTryingToUpdate && !isTryingToDelete) {
          msg = i18n.global.t(errorMessages.updating);
        } else if (errorMessages.deleting && !isTryingToAdd && !isTryingToUpdate && isTryingToDelete) {
          msg = i18n.global.t(errorMessages.deleting);
        } else if (errorMessages.overall) {
          msg = i18n.global.t(errorMessages.overall);
        }
        if (msg) {
          if (shouldWarnUserOnError && isCurrentRouteSameAsOrigin) {
            ErrorHandler.showError(msg, err);
          }
        }
      }
      if (isCurrentRouteSameAsOrigin) throw err;
    } finally {
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
    }
    return null;
  };
}

export default {};
